import { PiggyBank } from "lucide-react";
import type { AiUsageTotals } from "@mycompbox/shared";
import { useAiUsage } from "@/hooks/queries/use-ai-settings";

function usd(value: number): string {
  return value < 0.01 && value > 0 ? "<$0.01" : `$${value.toFixed(2)}`;
}

function tokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function Totals({ label, totals }: { label: string; totals: AiUsageTotals }) {
  const inputTotal = totals.inputTokens + totals.cacheReadTokens + totals.cacheWriteTokens;
  return (
    <div className="space-y-2 rounded-lg border border-white/[0.08] p-3">
      <p className="text-sm font-medium">{label}</p>
      {totals.calls === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing yet this month.</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-muted-foreground">Spent</dt>
              <dd className="font-medium">{totals.costUsd !== null ? usd(totals.costUsd) : "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Runs</dt>
              <dd className="font-medium">{totals.runs}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Tokens in / out</dt>
              <dd className="font-medium">
                {tokens(inputTotal)} / {tokens(totals.outputTokens)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">From cache</dt>
              <dd className="font-medium">{Math.round(totals.cachedShare * 100)}%</dd>
            </div>
          </dl>
          {totals.savedUsd !== null && totals.savedUsd > 0 ? (
            <p className="flex items-center gap-2 text-sm text-emerald-300">
              <PiggyBank className="h-4 w-4" />
              CompBox saved you {usd(totals.savedUsd)} by reusing cached instructions instead of resending them.
            </p>
          ) : null}
          {totals.unpricedCalls > 0 ? (
            <p className="text-xs text-muted-foreground">
              {totals.unpricedCalls} call{totals.unpricedCalls === 1 ? "" : "s"} used a model CompBox doesn't have a price for, so
              {totals.unpricedCalls === 1 ? " it's" : " they're"} counted in tokens only.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * What CompBox spent through the person's API key this month, and what prompt
 * caching saved. Spend is the provider's own figure where it reports one
 * (OpenRouter) and otherwise estimated from list prices.
 */
export function AiUsageSection() {
  const { data: usage } = useAiUsage();
  if (!usage) return null;
  const month = new Date(usage.since).toLocaleDateString(undefined, { month: "long", timeZone: "UTC" });
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Usage in {month}</p>
      <Totals label="Your runs" totals={usage.mine} />
      {usage.companyKey ? <Totals label="Everyone on the company key" totals={usage.companyKey} /> : null}
      <p className="text-xs text-muted-foreground">
        Only work CompBox runs on your key is counted here. Amounts are estimates from list prices unless the provider reports
        the exact cost.
      </p>
    </div>
  );
}
